import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { Zone } from 'src/features/zone/utils/types';
import ZoneRuleForm from 'src/features/membership/components/forms/zoneRule/ZoneRuleForm';
import {
    zoneRuleFormSchema,
    ZoneRuleFormSchemaValues,
} from 'src/features/membershipType/formSchema';
import { ZoneRule } from 'src/features/membershipType/types';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { cn } from 'src/utils/shadcn';
import { useListZones } from 'src/features/zone/api/useListZones';
import {
    useListMembershipZoneRules
} from 'src/features/membership/api/zoneRule/useListMembershipZoneRules';
import { useGetMembershipZoneRule } from 'src/features/membership/api/zoneRule/useGetMembershipZoneRule';
import { useParking } from 'src/providers/ParkingProvider';

interface ZoneRuleFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedZoneRule?: ZoneRule;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: ZoneRuleFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function MembershipZoneRuleFormModal<TData>({
    selectedZoneRule,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: ZoneRuleFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { locale } = useIntl();
    const { membershipId = '' } = useParams();

    const { parkingId } = useParking();
    const { data: listZonesData } = useListZones({ parkingId });

    const { data: zoneRuleData } = useGetMembershipZoneRule({
        parkingId,
        membershipId,
        ruleId: selectedZoneRule?.id ?? '',
        config: { enabled: !!selectedZoneRule?.id && isModalOpen && !!membershipId },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return zoneRuleData ? {
            ...zoneRuleData,
            zone: zoneRuleData.zone.id,
        } : {};
    }, [zoneRuleData]);

    const { data: allZoneRules } = useListMembershipZoneRules({
        parkingId,
        membershipId,
        config: {
            enabled: !!membershipId,
        }
    });

    const idsOfZonesWithRules = allZoneRules?.results?.map((zone) => zone.zone.id) ?? [];

    const filteredEmptyZoneTranslations = React.useMemo(() => {
        return listZonesData?.results?.filter((zone) => zone.translations.length > 0) ?? [];
    }, [listZonesData?.results]);

    const filteredZonesWithRules = filteredEmptyZoneTranslations?.filter(
        (zone) => !idsOfZonesWithRules.includes(zone.id),
    );

    const zoneListToUse: Zone[] = selectedZoneRule ? filteredEmptyZoneTranslations : filteredZonesWithRules;

    const zoneOptions = React.useMemo(
        () =>
            zoneListToUse?.map((zone) => {
                const translatedZone = zone.translations.find((z) => z.language === locale);

                return { value: zone.id, label: translatedZone?.name };
            }),
        [zoneListToUse, locale],
    );

    const form = useForm<ZoneRuleFormSchemaValues>({
        resolver: zodResolver(zoneRuleFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [zoneRuleData, isModalOpen, form, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: ZoneRuleFormSchemaValues) => {
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },

        [mutate, form, onError],
    );
    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)} disabled={zoneOptions.length === 0}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader className="flex flex-row gap-2">
                    <SquareIcon icon={icon} />
                    <div>
                        <DialogTitle>{title}</DialogTitle>
                        <DialogDescription>{subtitle}</DialogDescription>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <ZoneRuleForm form={form} onSubmit={onSubmit} zoneOptions={zoneOptions} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedZoneRule && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default MembershipZoneRuleFormModal;
